import { FormControl } from '@angular/forms';

export interface MissionInitiator {
  initiator_id: string;
  initiator_type: string;
  initiator_name: string;
}

export interface PresaleDetails {
  order_id: number;
  kind: string;
  deal_id: number;
  client_name: string;
  address: string;
  phone: string;
  comment: string;
  manager: string;
  manager_phone: string;
}

export interface MissionDocument {
  name: string;
  url: string;
}

export interface ObjectChangesDetails {
  kind: 'object_changes';
  documents: MissionDocument[];
}

export type MissionPriority = 'normal' | 'elevated' | 'high' | 'critical';

export interface Mission {
  id: string;
  priority?: MissionPriority;
  owner_dispatcher_id?: string | null;
  number: number;
  created_at: number;
  team_name?: string;
  address?: string;
  problem?: string;
  l2_dispatcher_commentary?: string;
  scheduling_commentary?: string | null;
  cancelation_reason?: string | null;
  bitrix_id?: number;
  object_number?: number;
  object_description?: string;
  object_name?: string;
  responsible?: string;
  responsible_person_phone?: string;
  presale_details?: PresaleDetails;
  object_changes?: ObjectChangesDetails;
  status_history?: MissionStatusHistory[];
  statuses?: MissionStatusHistory[];
  mission_type: MissionType | string;
  mission_subtype: ClientServiceSubtype | SupportServiceSubtype | TechnicianMissionSubtype | string;
  current_status: MissionStatus;
  is_exact_time: boolean;
  timeslots_enabled?: boolean;
  reserved_hours?: number;
  planned_start_datetime: number;
  planned_end_datetime?: number | null;
  actual_start_datetime?: number;
  checkpoints: Checkpoint[];
  executor_info: {
    assigned_at?: number;
    executor_id: string;
    executor_type: string;
  };
  initiator_info?: MissionInitiator | null;
}

export interface MissionStatusHistory {
  status: MissionStatus;
  created_at?: number;
  occured_at?: number;
  comment?: string;
  dispatcher_commentary?: string;
  initiator?: MissionInitiator | null;
}

export interface Checkpoint {
  id?: string;
  address: string;
  object_description?: string;
  object_name?: string;
  object_id?: number;
  object_number?: number;
  latitude?: number;
  longitude?: number;
  is_approximate?: boolean;
  tasks: Array<{
    name: string;
    number: number;
    is_selected?: boolean | FormControl<boolean>;
    id?: string;
    selected_at?: number;
  }>;
  checkpoint_number: number;
  planned_arrival_datetime: number;
  actual_arrival_datetime?: number;
  completed_at?: number;
  photos?: Array<{
    id?: string;
    name: string;
    value?: string | null;
  }>;
  documents?: Array<{
    name: string;
    value?: string;
    url?: string;
    type?: string;
    created_at?: number;
  }>;
  commentary?: FormControl<string>;
}

export interface MissionCheckpointFillRequest {
  id: string;
  tasks: string[];
  photos: Array<{
    name: string;
  }>;
}

export interface MissionCreateRequest {
  mission_type?: Mission['mission_type'];
  mission_subtype?: Mission['mission_subtype'];
  problem?: string;
  executor_info?: {
    executor_type: string;
    executor_id: string;
  };
  checkpoints?: Array<{
    address: string;
    geometry: {
      coordinates: number[];
      is_approximate: boolean;
    };
    object_description: string;
    object_id: number;
    tasks: Array<{
      name: string;
      number: number;
    }>;
    checkpoint_number: number;
    planned_arrival_datetime?: number;
    photos: Array<{
      name: string;
    }>;
  }>;
  planned_start_datetime?: number;
  planned_end_datetime?: number | null;
  timeslot?: MissionTimeslotRequest;
}

export interface ObjectChangesMissionCreateRequest {
  mission_type: MissionType.TechnicianMission;
  mission_subtype: TechnicianMissionSubtype.ObjectChanges;
  problem: string;
  checkpoints: Array<{
    address: string;
    object_id: number;
    object_description: string;
    checkpoint_number: number;
    tasks: Array<{ name: string; number: number }>;
    photos: Array<{ name: string }>;
  }>;
}

export type MissionTimeslotKind = 'hard' | 'soft';

export interface MissionTimeslotRequest {
  day_id: string;
  kind: MissionTimeslotKind;
  duration_hours?: number;
}

export interface MissionExecutorTimeslotInterval {
  from: string;
  to: string;
  hours: number;
}

export interface MissionExecutorTimeslotBooking {
  id: string;
  mission_id?: string;
  mission_number?: number;
  object_number?: string;
  address?: string;
  kind: MissionTimeslotKind;
  start_at: string;
  duration_hours: number;
  activity: {
    type: string;
    name: string;
  };
}

export interface MissionExecutorTimeslotDay {
  id: string;
  executor_id?: string;
  fieldworker_id?: string;
  executor_name?: string;
  date: string;
  window_from: string;
  window_to: string;
  blocked?: boolean;
  free_hours: number;
  busy: MissionExecutorTimeslotInterval[];
  free: MissionExecutorTimeslotInterval[];
  bookings?: MissionExecutorTimeslotBooking[];
}

export interface MissionCurrentTimeslot {
  day_id: string;
  executor_id: string;
  kind: MissionTimeslotKind;
  start_at: string;
  duration_hours: number;
}

export interface MissionTimeslotResponse {
  recovery_pending: boolean;
  kind_change_supported?: boolean;
  timeslot?: MissionCurrentTimeslot;
  day?: MissionExecutorTimeslotDay;
}

export interface MissionL2CreateRequest {
  problem: string;
  address: string;
  object_number?: number;
}

export interface MissionTechnician {
  id: string;
  name: string;
  created_at: number;
  technician_type: MissionTechnicianType;
}

export interface MissionStatisticsCity {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface MissionStatisticsTechnicianStat {
  id: string;
  name: string;
  completed_tech_amount: number;
  completed_regulatory_amount: number;
}

export interface MissionStatisticsOperatorStat {
  id: string;
  name: string;
  completed_tech_missions: number;
}

export interface MissionStatisticsResponse {
  technicians_stats: {
    stats: MissionStatisticsTechnicianStat[];
  };
  operators_stats: {
    stats: MissionStatisticsOperatorStat[];
  };
  missions_stats: {
    cancelled_missions_amount: number;
    undone_regulatory_amount: number;
    undone_technician_daily: number;
    undone_technician_ott: number;
    undone_technician_ttf: number;
    undone_technician_mtf: number;
  };
}

export interface HistoryMission {
  id: string;
  number: number;
  team_name?: string;
  created_at: number;
  mission_type: MissionType;
  mission_subtype: ClientServiceSubtype | SupportServiceSubtype | TechnicianMissionSubtype | string;
  current_status: MissionStatus;
  is_exact_time: boolean;
  planned_start_datetime: number;
  planned_end_datetime: number | null;
  actual_start_datetime?: number;
  executor_info: {
    assigned_at: number;
    executor_id: string;
    executor_type: string;
  };
  checkpoints_infos: Array<{
    number: number;
    address: string;
    object_number?: number;
    object_description?: string;
  }>;
}

export enum MissionStatus {
  New = 'new',
  InProcessing = 'in_progress',
  AwaitingFeedback = 'awaiting_client_feedback',
  AwaitingApproval = 'awaiting_scheduling',
  Planned = 'planned',
  Active = 'active',
  AwaitingReview = 'awaiting_review',
  Rework = 'rework',
  Completed = 'completed',
  Closed = 'closed',
  Canceled = 'canceled',
}

export enum MissionType {
  ClientService = 'client_service',
  SupportService = 'support_service',
  TechnicianMission = 'technician_mission',
}

export enum MissionTechnicianType {
  Technician = 'technician',
  Brigade = 'brigade',
}

export enum ClientServiceSubtype {
  Patrol = 'patrol',
  Examination = 'examination',
  TemporaryOutpost = 'temporary_outpost',
  Escort = 'escort',
}

export enum TechnicianMissionSubtype {
  RegulatoryMission = 'regulatory_mission',
  CommonMission = 'common_mission',
  Demontage = 'demontage',
  Presale = 'presale',
  Montage = 'montage',
  ObjectChanges = 'object_changes',
}

export enum SupportServiceSubtype {
  OutpostCheck = 'outpost_check',
  SupervisedOutpostCheck = 'supervised_outpost_check',
  IntruderTransfer = 'intruder_transfer',
  AuthorizedPersonTransport = 'authorized_person_transport',
  TechnicalSupport = 'technical_support',
  ArrivalTimeMeasurement = 'arrival_time_measurement',
  ObjectAcquaintance = 'object_acquaintance',
  ObjectActualization = 'object_actualization',
  DocumentDelivery = 'document_delivery',
  ObjectBriefing = 'object_briefing',
  GuardTransport = 'guard_transport',
  JointPatrol = 'joint_patrol',
  EmergencyStateSupport = 'emergency_state_support',
  TechnicalMaintenance = 'technical_maintenance',
}

export enum MissionDrawerType {
  ViewingInfo = 'viewing_info',
  ViewingReport = 'viewing_report',
  FillingReport = 'filling_report',
  FillingComment = 'filling_comment',
}
